'use client';

import { FC, memo, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Heart, Camera, MapPin, User, ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import RatingBadge from './RatingBadge';
import SafeImage, { DEFAULT_WORKSPACE_IMAGE } from './SafeImage';
import { useLoadableImages } from '@/hotdesk-ui/hooks/useLoadableImages';
import { ImageSize, sizedImageUrl } from '@/hotdesk-ui/helpers/image-url';
import { CARD_IMAGE_MOBILE_MEDIA, CARD_IMAGE_SIZES } from '@/hotdesk-ui/helpers/card-image-sizes';
import { ReadOnlyListing } from 'hotdesk_typescript';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { currencyConvert } from '@/hotdesk-ui/helpers/utils';
import { getCitySlug } from '@/hotdesk-ui/helpers/workspace-url';
import { NumberFormatService } from '@/hotdesk-ui/services/number-format.service';
import { firebaseAnalytics } from '@/hotdesk-ui/common-classes/firebase.service';
import { AuthPopupService } from '@/hotdesk-ui/services/authPopup.service';
import { useAppDispatch } from '@/hotdesk-ui/hooks/hook';
import { addFavorite, removeFavorite } from '@/hotdesk-ui/store/slices/favorites.slice';
import { showFavoriteToast } from '@/hotdesk-ui/helpers/favorite-toast';
import { getActiveUserSubscriptions, getCoveredSubscriptionName, getPreferredUserSubscriptionIds } from '@/hotdesk-ui/utils/subscriptionUtils';
import { getListingCardSubscriptionName, getMembershipDiscountedUnitPrice, getSubscriptionPromoDetails } from '@/hotdesk-ui/utils/listingSubscriptionUtils';
import { MyListing } from '@/hotdesk-ui/services/listing.service';
import AmenityRow from './AmenityRow';
import ScrollablePriceText from './ScrollablePriceText';
import TruncatedText from './TruncatedText';
import ProviderLabel from '@/hotdesk-ui/components/booking-v4/ProviderLabel';
import { parseRatingFromMetaData } from '@/hotdesk-ui/helpers/rating.utils';
import { resolveCardAmenityIds } from '@/hotdesk-ui/utils/amenities';
import { useWebReviewsDisplay } from '@/hotdesk-ui/hooks/useWebReviewsDisplay';
import { DistanceCalculatorService } from '@/hotdesk-ui/services/distance.calculator.service';
import type { WebReviewsDisplayScreen } from '@/hotdesk-ui/helpers/web-reviews-display';
import { isLongTermFrequency, preferredPriceUnitFromFrequency, shouldRequestIwgLongTermQuote, iwgBookableUnit, iwgBookableUnits, withBookableFrequency } from '@/hotdesk-ui/helpers/explore-location-params';
import { getListingProviderCode } from '@/hotdesk-ui/utils/provider';
import { useLongTermQuote } from '@/hotdesk-ui/components/long-term-quote/LongTermQuoteContext';
import styles from './ListingCard.module.scss';
export interface ListingCardProps {
  listing: ReadOnlyListing;
  variant?: 'workspace' | 'listing';
  /**
   * `grid`    – bordered card used on explore and the detail-page carousels.
   * `compact` – horizontal card used by the explore split view.
   * `minimal` – borderless landing card: rounded 4:3 photo, then title /
   *             city / price stacked underneath with no chrome.
   * `explore` – borderless explore-grid card: tall rounded photo with
   *             overlays (heart, membership, dots), then title / city /
   *             price or a Request Quote button.
   */
  layout?: 'grid' | 'compact' | 'minimal' | 'explore';
  showBookNow?: boolean;
  source?: string;
  membershipPassDiscountValue?: number;
  /** Override auto-detected display screen for rating badge gating. */
  reviewsScreen?: WebReviewsDisplayScreen;
  /**
   * Above-the-fold card (likely the LCP element): render the photo without
   * waiting for the visibility observer and fetch it at high priority.
   */
  priority?: boolean;
}
const getObjectKeys = (obj: any): string[] => Object.keys(obj || {});
const getBestPriceUnit = (priceKeys: string[]): string => {
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const fromFrequency = preferredPriceUnitFromFrequency(urlParams.get('frequency'), priceKeys);
  if (fromFrequency) return fromFrequency;
  if (priceKeys.includes('hour')) return 'hour';
  if (priceKeys.includes('day')) return 'day';
  if (priceKeys.includes('month')) return 'month';
  return 'annual';
};
const ListingCard: FC<ListingCardProps> = ({
  listing,
  variant = 'workspace',
  layout = 'grid',
  showBookNow = false,
  source,
  membershipPassDiscountValue = 0,
  reviewsScreen,
  priority = false
}) => {
  const t = useTranslations('landing-v4.card');
  const tRoot = useTranslations();
  const tExplore = useTranslations('explore');
  const tFavorites = useTranslations('favorites');
  const tFilters = useTranslations('filters.suggested');
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const longTermQuote = useLongTermQuote();
  const [isVisible, setIsVisible] = useState(priority);
  const imageAreaRef = useRef<HTMLDivElement>(null);
  // On touch devices the same horizontal gesture scrolls the surrounding
  // carousel, so swiping the photo track changes images by accident. There
  // the arrows are the only way to page through the gallery.
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(hover: none)');
    const update = () => setIsTouchDevice(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const resolvedReviewsScreen = useMemo((): WebReviewsDisplayScreen => {
    if (reviewsScreen) return reviewsScreen;
    const isHome = pathname === '/' || pathname === '/home' || !!pathname?.startsWith('/home/');
    if (variant === 'listing') {
      return isHome ? 'listing_home' : 'listing_explore';
    }
    return isHome ? 'workspace_home' : 'workspace_explore';
  }, [reviewsScreen, pathname, variant]);
  const showReviews = useWebReviewsDisplay(resolvedReviewsScreen);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      rootMargin: '200px'
    });
    if (imageAreaRef.current) {
      observer.observe(imageAreaRef.current);
    }
    return () => observer.disconnect();
  }, []);

  // Redux state
  const user = useAppSelector((state: RootState) => state.auth.user);
  const coveredWorkspaceIds = useAppSelector((state: RootState) => state.listing.coveredWorkspaceIds);
  const coveredListingIds = useAppSelector((state: RootState) => state.listing.coveredListingIds);
  const userSubscriptionIdIn = useAppSelector((state: RootState) => state.listing.filters?.userSubscriptionIdIn);
  const favoritesMap = useAppSelector((state: RootState) => state.favorites.items);
  const defaultCurrency = useAppSelector((state: RootState) => state.currency.defaultCurrency);
  const currencyList = useAppSelector((state: RootState) => state.currency.currency);
  const languageCode = useAppSelector((state: RootState) => state.listing.defaultLanguage);
  const amenitiesMap = useAppSelector((state: RootState) => state.amenities.items);

  // Derive favorite status from the store (O(1) lookup)
  const listingId = listing.id as number;
  const favoriteId = favoritesMap[listingId] ?? null;
  const isFavorite = favoriteId !== null;

  // Derive data from the raw listing
  const workspace = listing.workspace;
  const rules = listing.rules || workspace?.rules;
  const meta = variant === 'listing' ? listing.metaData || {} : workspace?.metaData || listing.metaData || {};

  // A workspace card is backed by a backend-chosen representative listing
  // (e.g. the cheapest desk on landing, a meeting room on explore). Its photos
  // are the *listing's*, so the same workspace looked different per page and
  // showed a single listing's images. Source the gallery from the workspace's
  // own images for workspace cards (listing fallback); listing cards stay
  // listing-first. Price/capacity intentionally keep using `rules` (the
  // representative listing) — `WorkspaceRules` carries no price.
  const workspaceImages: string[] = workspace?.rules?.images ? Object.values(workspace.rules.images).flat() as string[] : [];
  const listingImages: string[] = listing.rules?.images ? Object.values(listing.rules.images).flat() as string[] : [];
  const rawImages: string[] = variant === 'workspace' ? workspaceImages.length ? workspaceImages : listingImages : listingImages.length ? listingImages : workspaceImages;
  // Broken CDN images are dropped as they fail so the carousel only ever
  // shows photos that load; with none left this is the default photo.
  const {
    images,
    markBroken
  } = useLoadableImages(rawImages);
  const title = variant === 'listing' ? listing.title || workspace?.title || '' : workspace?.title || listing.title || '';
  const cityName = workspace?.workspaceCity?.name || listing.city?.name || '';
  // Same distance-from-user as the older workspace tiles.
  const [distance, setDistance] = useState(0);
  const lat = workspace?.location?.coordinates?.[1];
  const lon = workspace?.location?.coordinates?.[0];
  useEffect(() => {
    if (lat == null || lon == null) return;
    new DistanceCalculatorService().calculateDistanceFromCurrentLocation(lat, lon).then(({
      success,
      distance
    }) => {
      if (success) setDistance(distance);
    });
  }, [lat, lon]);
  const cityLabel = distance > 0 ? `${cityName} · ${distance.toFixed(0)}${tRoot('KM')}` : cityName;
  // Listing cards name the space under the listing: a listing title alone
  // ("first desk") says nothing about where it is. Workspace cards already
  // carry the workspace as their title.
  const workspaceSubtitle = variant === 'listing' && workspace?.title && workspace.title !== title ? workspace.title : '';
  const countrySlug = listing.city?.country?.slug?.toLowerCase() || '';
  const citySlug = getCitySlug(workspace?.workspaceCity || listing.city, cityName);
  const workspaceSlug = workspace?.slug || '';
  const listingSlug = listing.slug || '';
  // A workspace with a missing slug must not build `/workspaces/{country}/{city}/`
  // (empty last segment) — that silently resolves to the city explore page and
  // shows the "no spaces" empty state. Fall back to the workspace id, which the
  // detail page resolves via getWorkspaceById when the exact-slug lookup misses.
  const workspaceSegment = workspaceSlug || (workspace?.id != null ? String(workspace.id) : '');

  // Check for time-based filters (frequency = hour, day, etc.) or instant booking
  const frequency = searchParams.get('frequency');
  const quickBooking = searchParams.get('quickBooking');
  const hasTimeFilter = !!frequency;
  const hasInstantBooking = quickBooking === 'true';
  // Long-term Offices (month/year + day/hour in URL): IWG/Regus with no
  // month rate → Request Quote; month-priced Regus stays normal.
  const isIwgLongTermQuote = shouldRequestIwgLongTermQuote(listing, frequency, !!getListingProviderCode(listing));
  const preferredSubscriptionIds = getPreferredUserSubscriptionIds(userSubscriptionIdIn);

  // Build URL based on whether time filters or instant booking are active
  let url: string;
  if (hasTimeFilter || hasInstantBooking) {
    // If time filter or instant booking is active, navigate to listing details with filters preserved
    const listingUrl = `/workspaces/${countrySlug}/${citySlug}/${workspaceSegment}/${listingSlug}`;
    const params = new URLSearchParams();
    searchParams.forEach((value, key) => {
      params.set(key, value);
    });
    url = `${listingUrl}?${params.toString()}`;
  } else {
    // Original behavior: navigate to workspace/listing detail based on variant
    url = variant === 'listing' && listingSlug ? `/workspaces/${countrySlug}/${citySlug}/${workspaceSegment}/${listingSlug}` : `/workspaces/${countrySlug}/${citySlug}/${workspaceSegment}`;
  }

  // Amenities – listing ids first, then workspace (IWG/Regus often only set workspace)
  const rulesAny = rules as Record<string, unknown> | undefined;
  const metaAny = meta as Record<string, unknown> | undefined;
  const amenityIds = resolveCardAmenityIds(listing, variant === 'workspace');
  const resolvedAmenities = amenityIds.map(id => amenitiesMap[id]).filter(Boolean);
  const imgTitle = variant === 'listing' ? `${workspace?.title} ${listing?.title} in ${workspace?.workspaceCity?.name}` : `${workspace?.title} in ${workspace?.workspaceCity?.name}`;

  // Rating — listing cards use listing breakdown; workspace cards use workspace breakdown
  const ratingMeta = variant === 'listing' ? listing.metaData : workspace?.metaData || listing.metaData;
  const ratingParsed = parseRatingFromMetaData(ratingMeta);
  const rating = ratingParsed.displayRating ? Number(ratingParsed.displayRating) : 0;
  const reviewCount = ratingParsed.count;

  // Price
  const priceObj = (rulesAny?.price || {}) as Record<string, number>;
  const priceKeys = getObjectKeys(priceObj);
  const priceUnit = source == 'memberships' ? 'day' : getBestPriceUnit(priceKeys);
  const rawPrice = priceObj[priceUnit] || 0;
  const listingCurrency = (rulesAny?.currency || '') as string;
  const [displayPrice, setDisplayPrice] = useState('');
  const [membershipDiscountedPrice, setMembershipDiscountedPrice] = useState(0);
  const [displayCurrency, setDisplayCurrency] = useState('');
  const fallbackUserSubs = useMemo(() => getActiveUserSubscriptions(user), [user]);
  const {
    cap: promoCap
  } = useMemo(() => getSubscriptionPromoDetails(null, listing.type, preferredSubscriptionIds, fallbackUserSubs as unknown as Record<string, unknown>[]), [listing.type, preferredSubscriptionIds, fallbackUserSubs]);
  useEffect(() => {
    if (defaultCurrency?.code && rawPrice > 0) {
      let converted = currencyConvert(currencyList, listingCurrency, defaultCurrency.code, rawPrice);
      const formatter = new NumberFormatService();
      if (source === 'memberships' && membershipPassDiscountValue > 0) {
        converted = Math.max(0, converted - membershipPassDiscountValue);
      }
      setDisplayPrice(formatter.formatNumberWithCountingLabel(converted, languageCode));
      let convertedCap: number | null = null;
      if (promoCap != null) {
        convertedCap = currencyConvert(currencyList, listingCurrency, defaultCurrency.code, promoCap);
      }
      const membershipUnitPrice = getMembershipDiscountedUnitPrice(converted, convertedCap);
      setMembershipDiscountedPrice(membershipUnitPrice);
      setDisplayCurrency(defaultCurrency.code);
    } else {
      setDisplayPrice('');
      setMembershipDiscountedPrice(0);
    }
  }, [defaultCurrency, currencyList, rawPrice, listingCurrency, languageCode, source, membershipPassDiscountValue, promoCap]);

  // Guest count matches legacy ListingTile / ListingMapTile:
  // desks use rules.quantity; all other types use rules.capacity.
  // Workspace cards hide it — a workspace groups listings with different
  // capacities, so one number is misleading.
  const isDesk = listing.type === 1 || (listing as MyListing).listingTypeName === 'Desk';
  const guestCount = isDesk ? Number(rulesAny?.quantity) || 0 : Number(rulesAny?.capacity) || Number(metaAny?.capacity) || 0;
  const showGuests = variant === 'listing' && guestCount > 0;

  // Membership passes only cover hourly/daily bookings, so a card priced per
  // month/year — or any card under the Long-term Offices filter — never
  // shows the badge or the 0 price, even when the membership filter is on.
  const membershipEligible = (priceUnit === 'hour' || priceUnit === 'day') && !isLongTermFrequency(frequency);

  // Subscription "Applied" badge: workspace cards use workspace coverage;
  // listing cards only show it when this listing's type is on the plan.
  const subscriptionName = useMemo(() => {
    if (!membershipEligible) return null;
    return variant === 'listing' ? getListingCardSubscriptionName(user, listing as MyListing, coveredWorkspaceIds, coveredListingIds, userSubscriptionIdIn) : getCoveredSubscriptionName(user, variant, listing as MyListing, coveredWorkspaceIds, coveredListingIds, userSubscriptionIdIn);
  }, [membershipEligible, user, variant, listing, coveredWorkspaceIds, coveredListingIds, userSubscriptionIdIn]);
  const subscriptionApplicable = !!subscriptionName;

  // `quickBooking` is the backend's instant-booking flag — the same field
  // behind the explore filter of that name. It ships on the list payload,
  // so cards can badge it without a detail fetch.
  const instantBooking = !!listing.quickBooking;
  const currencyCode = displayCurrency || defaultCurrency?.code || '';
  const renderSubscriptionPriceValue = (priceRowClass: string, valueClass: string, unitClass: string, strikethroughClass: string) => {
    const unitLabel = t(priceUnit);
    if (membershipDiscountedPrice > 0) {
      return <span className={`${priceRowClass} ${styles.priceRowDiscounted}`}>
                    <span className={strikethroughClass}>
                        {currencyCode} {displayPrice}
                    </span>
                    <span className={valueClass}>
                        {membershipDiscountedPrice.toFixed(2)}
                    </span>
                    <span className={unitClass}>
                        {currencyCode} / {unitLabel}
                    </span>
                </span>;
    }
    return <span className={priceRowClass} data-sentry-component="renderSubscriptionPriceValue" data-sentry-source-file="ListingCard.tsx">
                <span className={valueClass}>
                    {currencyCode} 0
                </span>
                <span className={unitClass}>
                    / {unitLabel}
                </span>
            </span>;
  };

  // Image carousel state
  const [activeSlide, setActiveSlide] = useState(0);
  const touchStartX = useRef(0);
  const maxSlide = images.length - 1;
  const extraPhotos = images.length > 1 ? images.length - maxSlide : 0;
  const goToSlide = useCallback((index: number) => {
    setActiveSlide(Math.max(0, Math.min(index, maxSlide)));
  }, [maxSlide]);

  // Keep the active slide in range when a broken image is removed.
  useEffect(() => {
    if (activeSlide > maxSlide) setActiveSlide(Math.max(0, maxSlide));
  }, [activeSlide, maxSlide]);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    const threshold = 50;
    if (delta < -threshold) goToSlide(activeSlide + 1);else if (delta > threshold) goToSlide(activeSlide - 1);
  };
  const swipeHandlers = isTouchDevice ? {} : {
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd
  };
  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    goToSlide(activeSlide - 1);
  };
  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    goToSlide(activeSlide + 1);
  };
  const handleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) {
      new AuthPopupService().getPopup();
      return;
    }
    try {
      if (isFavorite && favoriteId) {
        await dispatch(removeFavorite({
          listingId,
          favoriteId
        })).unwrap();
        firebaseAnalytics('listing_unfavorited', {
          listingId
        });
        showFavoriteToast({
          message: tFavorites('removedToast'),
          viewAll: tFavorites('viewAll')
        });
      } else {
        await dispatch(addFavorite({
          userId: user.id!,
          listingId
        })).unwrap();
        firebaseAnalytics('listing_favorited', {
          listingId
        });
        showFavoriteToast({
          message: tFavorites('addedToast'),
          viewAll: tFavorites('viewAll')
        });
      }
    } catch (error) {
      console.error('Error updating favorite:', error);
    }
  };
  const handleClick = () => {
    firebaseAnalytics('workspace_clicked');
  };
  const isCompact = layout === 'compact';
  const isMinimal = layout === 'minimal';
  const isExplore = layout === 'explore';
  const hasBookNow = !isCompact && !isMinimal && !isExplore && showBookNow;
  const opensListingDetails = hasTimeFilter || hasInstantBooking || variant === 'listing' && !!listingSlug;
  const linkTarget = opensListingDetails || variant === 'workspace' ? '_blank' : undefined;
  const linkRel = opensListingDetails || variant === 'workspace' ? 'noopener noreferrer' : undefined;

  // The swipeable photo track is identical across layouts.
  const imageSizes = CARD_IMAGE_SIZES[layout];
  // The default photo (all of a card's photos broken) has no size variants.
  const cardImageUrl = (src: string, size: ImageSize) => src === DEFAULT_WORKSPACE_IMAGE ? src : sizedImageUrl(src, size);
  // Hosts without size variants (e.g. Regus) give the same URL for both.
  const mobileImageUrl = (src: string) => {
    const mobile = cardImageUrl(src, imageSizes.mobile);
    return mobile !== cardImageUrl(src, imageSizes.desktop) ? mobile : undefined;
  };
  const imageTrack = isVisible && images.length > 0 && <div className={styles.imageTrack} style={{
    transform: `translateX(-${activeSlide * 100}%)`
  }}>
            {images.map((src, idx) => {
      const shouldRender = idx === activeSlide || idx === activeSlide - 1 || idx === activeSlide + 1;
      return <div key={src} className={styles.imageSlide}>
                        {shouldRender &&
        // Images are unoptimized (next.config), so `sizes`
        // alone still downloads the full-size original —
        // request a size variant per viewport (see
        // helpers/card-image-sizes); SafeImage falls back
        // to the original if a variant is missing.
        <SafeImage src={cardImageUrl(src, imageSizes.desktop)} mobileSrc={mobileImageUrl(src)} mobileMedia={CARD_IMAGE_MOBILE_MEDIA} alt={`${imgTitle} ${idx + 1}`} fill className={styles.image} sizes="(max-width: 640px) 280px, 340px" {...priority && idx === 0 ? {
          priority: true
        } : {
          loading: idx === 0 ? 'eager' : 'lazy'
        }} onAllFailed={() => markBroken(src)} />}
                    </div>;
    })}
        </div>;

  // ── Minimal layout (landing) ────────────────────────────────
  if (isMinimal) {
    return <Link href={url} className={styles.cardMinimal} onClick={handleClick} target={linkTarget} rel={linkRel} draggable={false}>
                <div ref={imageAreaRef} className={styles.minimalImageArea} {...swipeHandlers}>
                    {imageTrack}

                    {images.length > 1 && activeSlide > 0 && <button className={`${styles.slideArrow} ${styles.slideArrowLeft}`} onClick={handlePrev} type="button" aria-label="Previous image">
                            <ChevronLeft size={16} />
                        </button>}
                    {images.length > 1 && activeSlide < maxSlide && <button className={`${styles.slideArrow} ${styles.slideArrowRight}`} onClick={handleNext} type="button" aria-label="Next image">
                            <ChevronRight size={16} />
                        </button>}

                    <div className={styles.minimalOverlaysLeft}>
                        {!!rating && <RatingBadge rating={rating} reviewCount={reviewCount} size="sm" />}
                        {/* Icon-only at rest; the label slides out of it when
                            the card is hovered. `title` keeps it discoverable
                            for touch/keyboard, where there is no hover. */}
                        {subscriptionApplicable && <span className={styles.minimalSubscriptionBadge} title={t('subscriptionApplicable')}>
                                <span className={styles.minimalSubscriptionIcon}>
                                    <Image src="/assets/icons/white-check.svg" alt="" width={12} height={12} />
                                </span>
                                <span className={styles.minimalSubscriptionText}>
                                    {t('subscriptionApplicable')}
                                </span>
                            </span>}
                        {/* Same collapsed-icon treatment as the membership
                            badge above, so the two stack consistently. */}
                        {instantBooking && <span className={styles.minimalSubscriptionBadge} title={tFilters('instantBooking')}>
                                <span className={styles.minimalSubscriptionIcon}>
                                    <Zap size={12} fill="currentColor" />
                                </span>
                                <span className={styles.minimalSubscriptionText}>
                                    {tFilters('instantBooking')}
                                </span>
                            </span>}
                    </div>

                    {/* Favourites are listing-scoped in the backend, so a
                        workspace card has nothing to toggle. */}
                    {variant === 'listing' && <button className={styles.minimalFavoriteBtn} onClick={handleFavorite} aria-label="Add to favorites" type="button">
                            <Heart size={20} color="#fff" fill={isFavorite ? '#000' : 'rgba(0, 0, 0, 0.25)'} />
                        </button>}
                </div>

                <div className={styles.minimalInfo}>
                    {/* Row wrapper so the title's `flex: 1 0 0` applies on the
                        main axis — `.minimalInfo` is a column. */}
                    <h3 className={styles.minimalTitleRow}>
                        <TruncatedText text={title} className={styles.minimalTitle} />
                    </h3>
                    {workspaceSubtitle && <p className={styles.workspaceSubtitle} title={workspaceSubtitle} dir="auto">
                            {workspaceSubtitle}
                        </p>}
                    <p className={styles.minimalCity}>{cityLabel}</p>
                </div>

                {(displayPrice || showGuests || subscriptionName) && <div className={styles.minimalPriceRow}>
                        {(displayPrice || subscriptionName) && <p className={styles.minimalPrice}>
                                {subscriptionName ? <>
                                        <span className={styles.minimalPriceLabel} title={`${subscriptionName} ${tExplore('applied')}`}>
                                            {subscriptionName}{' '}
                                            {tExplore('applied')}
                                        </span>
                                        {renderSubscriptionPriceValue(styles.minimalPriceRowInner, styles.minimalPriceValue, styles.minimalPriceUnit, styles.minimalPriceStrikethrough)}
                                    </> : <>
                                        <span className={styles.minimalPriceLabel}>
                                            {t('startingFrom')}
                                        </span>
                                        <span className={styles.minimalPriceValue}>
                                            {displayPrice} {displayCurrency}/{t(priceUnit)}
                                        </span>
                                    </>}
                            </p>}
                        {showGuests && <span className={styles.guestCount} aria-label={`${guestCount}`}>
                                <User size={14} strokeWidth={2} />
                                {guestCount}
                            </span>}
                    </div>}
            </Link>;
  }

  // ── Explore-grid layout ─────────────────────────────────────
  if (isExplore) {
    // Quote-only for IWG/Regus under long-term filters (month/annual) with
    // no monthly rate — not for every workspace missing a display price.
    const isQuoteOnly = isIwgLongTermQuote;

    // The quote card also offers the listing's own day/hour rate: the
    // card (and the Book half) opens details on that unit, the Request
    // Quote half opens the long-term quote form. With no day/hour rate the
    // whole card stays a quote request.
    const canBookUnit = isQuoteOnly && !!iwgBookableUnit(listing.rules?.price);
    const quoteOnlyCard = isQuoteOnly && !canBookUnit;
    const bookableUnitsLabel = iwgBookableUnits(listing.rules?.price).map(unit => t(unit === 'hour' ? 'hourly' : 'daily')).join('/');
    const exploreUrl = canBookUnit ? withBookableFrequency(url, listing.rules?.price) : url;
    const handleRequestQuote = (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!longTermQuote) return;
      longTermQuote.requestQuote({
        spaceName: workspace?.title || '',
        listingName: listing.title || '',
        city: workspace?.workspaceCity?.name || listing.city?.name || '',
        listingId: listing.id,
        workspaceId: workspace?.id,
        listingTypeId: listing.type ?? null,
        source: 'IWG'
      });
    };
    return <Link href={exploreUrl} className={styles.cardExplore} onClick={quoteOnlyCard ? handleRequestQuote : handleClick} target={quoteOnlyCard ? undefined : linkTarget} rel={quoteOnlyCard ? undefined : linkRel} draggable={false}>
                <div ref={imageAreaRef} className={styles.exploreImageArea} {...swipeHandlers}>
                    {imageTrack}

                    {images.length > 1 && activeSlide > 0 && <button className={`${styles.slideArrow} ${styles.slideArrowLeft}`} onClick={handlePrev} type="button" aria-label="Previous image">
                            <ChevronLeft size={16} />
                        </button>}
                    {images.length > 1 && activeSlide < maxSlide && <button className={`${styles.slideArrow} ${styles.slideArrowRight}`} onClick={handleNext} type="button" aria-label="Next image">
                            <ChevronRight size={16} />
                        </button>}

                    <div className={styles.exploreOverlaysLeft}>
                        {!!rating && <RatingBadge rating={rating} reviewCount={reviewCount} />}
                        {/* Under the rating: icon pill that expands inline to
                            "Membership Applicable" on card hover, matching the
                            landing card. */}
                        {subscriptionApplicable && <span className={styles.exploreMembershipBadge}>
                                <span className={styles.exploreMembershipIcon}>
                                    <Image src="/assets/icons/white-check.svg" alt="" width={12} height={12} />
                                </span>
                                <span className={styles.exploreMembershipText}>
                                    {t('subscriptionApplicable')}
                                </span>
                            </span>}
                        {/* Same collapsed-icon treatment as the membership
                            badge above, so the two stack consistently. */}
                        {instantBooking && <span className={styles.exploreMembershipBadge} title={tFilters('instantBooking')}>
                                <span className={styles.exploreMembershipIcon}>
                                    <Zap size={12} fill="currentColor" />
                                </span>
                                <span className={styles.exploreMembershipText}>
                                    {tFilters('instantBooking')}
                                </span>
                            </span>}
                    </div>

                    {/* Favourites are listing-scoped in the backend, so a
                        workspace card has nothing to toggle. */}
                    {variant === 'listing' && <button className={styles.minimalFavoriteBtn} onClick={handleFavorite} aria-label="Add to favorites" type="button">
                            <Heart size={20} color="#fff" fill={isFavorite ? '#000' : 'rgba(0, 0, 0, 0.25)'} />
                        </button>}

                    {images.length > 1 && <div className={styles.exploreDots}>
                            {images.slice(0, 5).map((_, idx) => <span key={idx} className={`${styles.exploreDot} ${idx === activeSlide ? styles.exploreDotActive : ''}`} />)}
                        </div>}
                </div>

                <div className={styles.exploreInfo}>
                    <div className={styles.exploreTitleRow}>
                        <h3 className={styles.exploreTitle}>
                            <TruncatedText text={title} className={styles.exploreTitleText} />
                        </h3>
                    </div>
                    {workspaceSubtitle && <p className={styles.workspaceSubtitle} title={workspaceSubtitle} dir="auto">
                            {workspaceSubtitle}
                        </p>}
                    <p className={styles.exploreCity}>{cityLabel}</p>

                    {isQuoteOnly ? <div className={styles.exploreQuoteSplit}>
                            <button type="button" className={styles.exploreQuoteBtn} onClick={handleRequestQuote}>
                                <span className={styles.exploreQuoteBtnSub}>
                                    {t('longTermTerms')}
                                </span>
                                <span className={styles.exploreQuoteBtnTitle}>
                                    {t('requestQuote')}
                                </span>
                            </button>
                            {/* Not a button: the click falls through to the
                                card link, which already targets the unit. */}
                            {canBookUnit && <span className={`${styles.exploreQuoteBtn} ${styles.exploreBookBtn}`}>
                                    <span className={styles.exploreQuoteBtnSub}>
                                        {bookableUnitsLabel}
                                    </span>
                                    <span className={styles.exploreQuoteBtnTitle}>
                                        {t('instantBooking')}
                                    </span>
                                </span>}
                        </div> : (displayPrice || showGuests || subscriptionName) && <div className={styles.explorePriceRow}>
                                {(displayPrice || subscriptionName) && (subscriptionName ? <p className={styles.explorePrice}>
                                            <span className={styles.explorePriceLabel} title={`${subscriptionName} ${tExplore('applied')}`}>
                                                {subscriptionName}{' '}
                                                {tExplore('applied')}
                                            </span>
                                            {renderSubscriptionPriceValue(styles.explorePriceRowInner, styles.explorePriceValue, styles.explorePriceUnit, styles.explorePriceStrikethrough)}
                                        </p> : displayPrice && (variant === 'workspace' ? <p className={styles.explorePrice}>
                                                <span className={styles.explorePriceLabel}>
                                                    {t('startingFrom')}
                                                </span>
                                                <span className={styles.explorePriceValue}>
                                                    {displayCurrency} {displayPrice}/
                                                    {t(priceUnit)}
                                                </span>
                                            </p> : <p className={styles.explorePrice}>
                                                <span className={styles.explorePriceValue}>
                                                    {displayPrice} {displayCurrency}/
                                                    {t(priceUnit)}
                                                </span>
                                            </p>))}
                                {showGuests && <span className={styles.guestCount} aria-label={`${guestCount}`}>
                                        <User size={14} strokeWidth={2} />
                                        {guestCount}
                                    </span>}
                            </div>}
                </div>
            </Link>;
  }
  return <Link href={url} className={`${styles.card} ${isCompact ? styles.cardCompact : ''} ${hasBookNow ? styles.cardWithCta : ''}`} onClick={handleClick} target={linkTarget} rel={linkRel} draggable={false} data-sentry-element="Link" data-sentry-component="ListingCard" data-sentry-source-file="ListingCard.tsx">
            {/* ── Image area ── */}
            <div ref={imageAreaRef} className={styles.imageArea} {...swipeHandlers}>
                {imageTrack}

                {/* Arrow navigation (desktop hover) */}
                {images.length > 1 && activeSlide > 0 && <button className={`${styles.slideArrow} ${styles.slideArrowLeft}`} onClick={handlePrev} type="button" aria-label="Previous image">
                        <ChevronLeft size={16} />
                    </button>}
                {images.length > 1 && activeSlide < maxSlide && <button className={`${styles.slideArrow} ${styles.slideArrowRight}`} onClick={handleNext} type="button" aria-label="Next image">
                        <ChevronRight size={16} />
                    </button>}

                {/* Top-left overlays */}
                <div className={styles.overlaysLeft}>
                    {showReviews && !!rating && <span className={styles.ratingBadge}>
                             <RatingBadge rating={rating} reviewCount={reviewCount} />
                         </span>}
                    {subscriptionApplicable && <span className={styles.subscriptionBadge}>
                            <span className={styles.subscriptionIcon}>
                                <Image src="/assets/icons/white-check.svg" alt="subscription-applied" width={20} height={20} />
                            </span>
                            {t('subscriptionApplicable')}
                        </span>}
                </div>

                {/* Top-right favorite (listings only — backend has no workspace favorites) */}
                {variant === 'listing' && <button className={styles.favoriteBtn} onClick={handleFavorite} aria-label="Add to favorites" type="button">
                        <Heart size={16} color={isFavorite ? '#000' : '#666'} fill={isFavorite ? '#000' : 'none'} />
                    </button>}

                {/* Bottom-center dots */}
                {images.length > 1 && <div className={styles.dots}>
                        {images.map((_, idx) => <span key={idx} className={`${styles.dot} ${idx === activeSlide ? styles.dotActive : ''}`} />)}
                    </div>}

                {/* Bottom-right photo count */}
                {extraPhotos > 0 && <span className={styles.photoCount}>
                        <Camera size={12} />
                        +{extraPhotos}
                    </span>}
            </div>

            {/* ── Content area ── */}
            <div className={styles.content}>
                {/* Amenities row (grid only) */}
                {!isCompact && resolvedAmenities.length > 0 && <div className={styles.gridAmenities}>
                        <AmenityRow amenities={resolvedAmenities} iconSize={18} />
                    </div>}

                {/* Title row */}
                <div className={styles.titleRow}>
                    {!isCompact && <h3 className={variant === 'listing' ? styles.title + ' ' + styles.titleListing : styles.title} title={title} dir="auto">
                            {title}
                        </h3>}
                    {isCompact && <>
                            <h3 className={variant === 'listing' ? styles.title + ' ' + styles.titleListingClamp : styles.title} title={title} dir="auto">
                                {title}
                            </h3>
                            <AmenityRow amenities={resolvedAmenities} iconSize={16} className={styles.compactAmenities} />
                        </>}
                </div>
                {variant === 'listing' && workspace?.title && <div className={styles.titleRowListing}>
                        <span className={styles.workspaceTitle}>
                        {t('In')} {workspace.title}
                        </span>
                    </div>}

                {/* Compact: meta below title */}
                {/* Bottom: separator + meta(grid)/pricing */}
                <div className={styles.bottom}>
                    {!isCompact && <div className={styles.meta}>
                            <span className={styles.metaRow}>
                                <span className={styles.metaIcon}>
                                    <MapPin size={14} />
                                </span>
                                <span className={styles.metaText}>{cityLabel}</span>
                            </span>
                        </div>}

                    <div className={styles.priceGuestsRow}>
                        {subscriptionName ? <div className={styles.pricing}>
                                <span className={styles.pricingLabel} title={`${subscriptionName} ${tExplore('applied')}`}>
                                    {subscriptionName} {tExplore('applied')}
                                </span>
                                <span className={styles.priceRow}>
                                    {membershipDiscountedPrice > 0 ? <ScrollablePriceText title={`${currencyCode} ${displayPrice} → ${membershipDiscountedPrice.toFixed(2)}`}>
                                            {renderSubscriptionPriceValue(styles.priceRow, styles.priceValue, styles.priceUnit, styles.priceStrikethrough)}
                                        </ScrollablePriceText> : <ScrollablePriceText title={`${currencyCode} 0`}>
                                            <span className={styles.priceValue}>
                                                {currencyCode} 0
                                            </span>
                                            <span className={styles.priceUnit}>
                                                / {t(priceUnit)}
                                            </span>
                                        </ScrollablePriceText>}
                                </span>
                            </div> : displayPrice && <div className={styles.pricing}>
                                    {source === 'memberships' ? <span className={styles.pricingLabel}>
                                            {t('passApplied')}
                                        </span> : <span className={styles.pricingLabel}>
                                            {t('startingFrom')}
                                        </span>}
                                    <span className={styles.priceRow}>
                                        <ScrollablePriceText title={`${displayCurrency} ${displayPrice}`}>
                                            <span className={styles.priceValue}>
                                                {displayCurrency} {displayPrice}
                                            </span>
                                            <span className={styles.priceUnit}>
                                                /{' '}{t(priceUnit)}
                                            </span>
                                        </ScrollablePriceText>
                                    </span>
                                </div>}
                        {showGuests && <span className={styles.guestCount} aria-label={`${guestCount}`}>
                                <User size={14} strokeWidth={2} />
                                {guestCount}
                            </span>}
                    </div>
                </div>

                {hasBookNow && <span className={styles.bookNowBtn}>
                        {t('view')}
                    </span>}
            </div>
        </Link>;
};

/**
 * Memoised because the carousels re-render their whole child list far more
 * often than the data behind it changes: react-multi-carousel re-runs
 * `React.Children.toArray` on every state change, and its `window.resize`
 * listener fires on each of those — including the ones iOS Safari emits
 * every time the URL bar collapses or expands during a normal vertical
 * scroll. Without this, a single scroll re-ran every card on the page (ten
 * `useSelector`s, URL building, image mapping) dozens of times.
 *
 * All props are primitives apart from `listing`, which comes straight out of
 * the store, so the default shallow compare is enough.
 */
export default memo(ListingCard);